// ---------------------------------------------------------------
// legalDoc.js — Maquetación de los documentos acreditativos de la
// farmacia (consentimiento del fichaje, aviso de privacidad…).
//
// Una sola plantilla para todos: mismo membrete, misma tipografía y
// mismo bloque de firma. Cada documento sólo aporta su título, sus
// puntos y la fecha en que esa persona lo aceptó o lo leyó.
//
// Se abre en una ventana aparte y se lanza el diálogo de impresión,
// donde se elige "Guardar como PDF". Se hace así, y no con una
// librería de PDF, para no meter 300 KB en la web por un documento
// que se saca dos veces al año.
// ---------------------------------------------------------------

/** "5 de agosto de 2026 a las 10:24:07" */
function fechaLarga(date) {
  const d = date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const h = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${d} a las ${h}`;
}

const esc = (s) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

/**
 * Abre el justificante en una ventana aparte y lanza el diálogo de
 * impresión, donde se elige "Guardar como PDF". Se hace así, y no con
 * una librería de PDF, para no meter 300 KB en la web por un
 * documento que se saca dos veces al año.
 *
 * @param nombre     nombre de la persona
 * @param aceptadoEn Date de cuándo aceptó (null si no consta)
 * @param version    versión del texto aceptada
 */

/**
 * @param titulo        título del documento
 * @param entradilla    frase bajo el título
 * @param intro         párrafo destacado
 * @param puntos        [{ title, text }]
 * @param tituloPuntos  encabezado de la lista
 * @param declaracion   frase entrecomillada que se aceptó (opcional)
 * @param etiquetaFirma "Firmado y aceptado por" / "Leído y entendido por"
 * @param nombre        persona
 * @param fecha         Date de la aceptación (null si no consta)
 * @param version       versión del texto
 * @param notaFirma     letra pequeña bajo la firma
 */
export function abrirDocumentoAcreditativo({
  titulo,
  entradilla,
  intro,
  puntos,
  tituloPuntos = "Información facilitada",
  declaracion,
  etiquetaFirma,
  nombre,
  fecha,
  version,
  notaFirma,
}) {
  const lista = (puntos || [])
    .map(
      (p, i) => `
      <li class="punto">
        <span class="num">${i + 1}</span>
        <div>
          <h3>${esc(p.title)}</h3>
          <p>${esc(p.text)}</p>
        </div>
      </li>`
    )
    .join("");

  const firma = fecha
    ? `<div class="firma-nombre">${esc(nombre)}</div>
       <div class="firma-fecha">${esc(fechaLarga(fecha))}</div>`
    : `<div class="firma-nombre sin">Sin registro</div>
       <div class="firma-fecha">No consta que ${esc(nombre)} lo haya hecho.</div>`;

  const logo = `${window.location.origin}${process.env.PUBLIC_URL || ""}/logo.png`;

  const html = `<!doctype html>
<html lang="es"><head><meta charset="utf-8">
<title>${esc(titulo)} - ${esc(nombre)}</title>
<style>
  @page { size: A4; margin: 16mm 18mm; }
  /* Un documento oficial va en claro siempre: sin esto el navegador
     lo oscurece solo al previsualizarlo y se ve fatal */
  :root { color-scheme: only light; }
  * { box-sizing: border-box; }
  html { background: #e9edeb; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                 Helvetica, Arial, sans-serif;
    color: #1a1a1a; background: #fff; line-height: 1.62; font-size: 10.8pt;
    margin: 24px auto; max-width: 200mm; padding: 22mm 20mm;
    box-shadow: 0 6px 26px rgba(0, 0, 0, 0.13); border-radius: 4px;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  @media print {
    html { background: #fff; }
    body { margin: 0; padding: 0; max-width: none; box-shadow: none; border-radius: 0; }
  }

  header {
    display: flex; align-items: center; gap: 14px;
    padding-bottom: 14px; border-bottom: 2.5px solid #163832;
  }
  header img { width: 52px; height: 52px; border-radius: 11px; object-fit: cover; }
  .marca { flex: 1; }
  .marca .nom { font-size: 16pt; font-weight: 800; letter-spacing: -0.3px; color: #163832; }
  .marca .sub { font-size: 8.8pt; color: #6b7b74; letter-spacing: 0.4px; text-transform: uppercase; }
  .ref { text-align: right; font-size: 8.5pt; color: #6b7b74; line-height: 1.5; }

  h1 { font-size: 15pt; margin: 26px 0 4px; letter-spacing: -0.3px; }
  .lead { color: #55635e; font-size: 10pt; margin: 0 0 20px; }
  .intro {
    background: #f2f6f4; border-left: 3px solid #163832;
    padding: 12px 16px; border-radius: 0 8px 8px 0; margin-bottom: 22px;
  }

  h2 { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.8px;
       color: #6b7b74; margin: 0 0 12px; }
  ol.puntos { list-style: none; margin: 0; padding: 0; }
  .punto { display: flex; gap: 12px; margin-bottom: 15px; break-inside: avoid; }
  .punto .num {
    flex: none; width: 22px; height: 22px; border-radius: 50%;
    background: #163832; color: #fff; font-size: 9.5pt; font-weight: 700;
    display: flex; align-items: center; justify-content: center; margin-top: 1px;
  }
  .punto h3 { font-size: 10.8pt; margin: 0 0 2px; color: #163832; }
  .punto p { margin: 0; color: #34423d; }

  .decl {
    margin: 24px 0; padding: 14px 18px; border-radius: 8px;
    background: #fbfbfa; border: 1px solid #dfe5e2;
    font-style: italic; color: #34423d;
  }
  .decl b { font-style: normal; }

  .firma {
    margin-top: 26px; padding: 18px 20px; border-radius: 10px;
    border: 1.5px solid #163832; background: #f7faf9; break-inside: avoid;
  }
  .firma-label {
    font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.8px;
    color: #6b7b74; margin-bottom: 6px;
  }
  .firma-nombre { font-size: 14pt; font-weight: 800; color: #163832; }
  .firma-nombre.sin { color: #b42318; }
  .firma-fecha { font-size: 10pt; color: #34423d; margin-top: 2px; }
  .firma-nota { font-size: 8.5pt; color: #6b7b74; margin-top: 10px;
                padding-top: 10px; border-top: 1px dashed #cfd9d5; }

  footer { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e3e8e6;
           color: #7b8a84; font-size: 8.2pt; line-height: 1.5; }

  .aviso { margin-bottom: 16px; padding: 10px 14px; background: #fff8e1;
           border: 1px solid #e8d48b; border-radius: 8px; font-size: 10pt; }
  @media print { .aviso { display: none; } }
</style></head><body>

<div class="aviso">
  En el diálogo de impresión elige <b>Destino → Guardar como PDF</b>.
</div>

<header>
  <img src="${logo}" alt="">
  <div class="marca">
    <div class="nom">Farmacia Buendía</div>
    <div class="sub">Portal de gestión</div>
  </div>
  <div class="ref">
    Documento acreditativo${version ? `<br>Texto versión ${esc(version)}` : ""}
  </div>
</header>

<h1>${esc(titulo)}</h1>
<p class="lead">${esc(entradilla)}</p>

<div class="intro">${esc(intro)}</div>

<h2>${esc(tituloPuntos)}</h2>
<ol class="puntos">${lista}</ol>

${declaracion ? `<div class="decl"><b>Declaración aceptada:</b> "${esc(declaracion)}"</div>` : ""}

<div class="firma">
  <div class="firma-label">${esc(etiquetaFirma)}</div>
  ${firma}
  <div class="firma-nota">${esc(notaFirma)}</div>
</div>

<footer>
  Documento generado el ${esc(fechaLarga(new Date()))}.
  Reproduce íntegramente el texto de la versión${version ? ` ${esc(version)}` : ""}
  que esta persona tuvo delante.
</footer>

<script>
  // Esperar a que el logo esté cargado antes de imprimir, para que no
  // salga el hueco en blanco
  window.addEventListener("load", function () { setTimeout(function () { window.print(); }, 120); });
</script>
</body></html>`;

  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return false; // bloqueado por el navegador
  win.document.write(html);
  win.document.close();
  win.focus();
  return true;
}
